import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { IconCheck, IconPointFilled } from "@tabler/icons-react";
import clsx from "clsx";
import * as React from "react";

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

/*
 dropdown sub trigger
 */
const DropdownMenuSubTrigger = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> & {
		inset?: boolean;
	}
>(({ className, inset, children, ...props }, ref) => (
	<DropdownMenuPrimitive.SubTrigger
		ref={ref}
		className={clsx(
			"relative flex cursor-default select-none items-center rounded-md gap-2 text-base outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-30 text-foreground focus:text-foreground-dark focus:bg-primary-50",
			inset && "pl-8",
			className,
		)}
		{...props}
	>
		{children}
	</DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

/*
 dropdown content
 */
const DropdownMenuContent = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content>
>(({ className, sideOffset = 4, side = "bottom", align = "end", ...props }, ref) => (
	<DropdownMenuPrimitive.Portal>
		<DropdownMenuPrimitive.Content
			ref={ref}
			sideOffset={sideOffset}
			side={side}
			align={align}
			className={clsx(
				"bg-white z-[99] min-w-[180px] w-auto overflow-hidden rounded-md border border-border/50 bg-popover p-1 text-foreground shadow-lg shadow-black/10",
				"data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
				className,
			)}
			{...props}
		/>
	</DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

/*
 dropdown sub-content
 */
const DropdownMenuSubContent = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent>
>(({ className, ...props }, ref) => (
	<DropdownMenuPrimitive.SubContent
		ref={ref}
		className={clsx(
			"bg-white z-[99] min-w-[180px] w-auto overflow-hidden rounded-md border border-border/50 bg-popover p-1 text-foreground shadow-lg shadow-black/10",
			"data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
			className,
		)}
		{...props}
	/>
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

/*
 dropdown item
 */
const DropdownMenuItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Item>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & {
		inset?: boolean;
		dangerZone?: boolean;
	}
>(({ className, inset, dangerZone, ...props }, ref) => (
	<DropdownMenuPrimitive.Item
		ref={ref}
		className={clsx(
			"dropdown-menu-item relative flex cursor-default select-none items-center rounded-md gap-2 text-base outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-30",
			{
				"text-danger-600 focus:bg-danger-100/40": dangerZone,
				"text-foreground focus:text-foreground-dark focus:bg-primary-50": !dangerZone,
			},
			inset && "pl-8",
			className,
		)}
		{...props}
	/>
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

/*
 dropdown checkbox item
 */
const DropdownMenuCheckboxItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem>
>(({ className, children, checked, ...props }, ref) => (
	<DropdownMenuPrimitive.CheckboxItem
		ref={ref}
		checked={checked}
		className={clsx(
			"relative flex cursor-default select-none items-center rounded-md py-1 pr-2 pl-8 text-base outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-foreground focus:text-foreground-dark focus:bg-primary-50",
			className,
		)}
		{...props}
	>
		<DropdownMenuItemIndicator className="absolute left-2 justify-start items-center">
			<IconCheck size={16} stroke={2} />
		</DropdownMenuItemIndicator>
		<div className="flex w-full">{children}</div>
	</DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

/*
 dropdown radio item
 */
const DropdownMenuRadioItem = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem>
>(({ className, children, ...props }, ref) => (
	<DropdownMenuPrimitive.RadioItem
		ref={ref}
		className={clsx(
			"relative flex cursor-default select-none items-center rounded-md py-1 pr-2 pl-8 text-base outline-none transition-colors data-[disabled]:pointer-events-none data-[disabled]:opacity-50 text-foreground focus:text-foreground-dark focus:bg-primary-50",
			className,
		)}
		{...props}
	>
		<DropdownMenuItemIndicator className="absolute left-2 justify-start items-center">
			<IconPointFilled size={16} stroke={2} />
		</DropdownMenuItemIndicator>
		{children}
	</DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

/*
 dropdown group label
 */
const DropdownMenuLabel = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Label>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & {
		inset?: boolean;
	}
>(({ className, inset, ...props }, ref) => (
	<DropdownMenuPrimitive.Label ref={ref} className={clsx("px-2 py-1 text-sm font-semibold text-foreground/50", inset && "pl-8", className)} {...props} />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

/*
 dropdown group separator
 */
const DropdownMenuSeparator = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator>
>(({ className, ...props }, ref) => <DropdownMenuPrimitive.Separator ref={ref} className={clsx("-mx-1 my-1 h-px bg-slate-100", className)} {...props} />);
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

/*
 dropdown shortcut keys
 */
const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) => {
	return <span className={clsx("ml-auto text-base tracking-widest opacity-40", className)} {...props} />;
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

/*
 dropdown item indicator
 */
const DropdownMenuItemIndicator = React.forwardRef<
	React.ElementRef<typeof DropdownMenuPrimitive.ItemIndicator>,
	React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.ItemIndicator>
>(({ className, children, ...props }, ref) => (
	<DropdownMenuPrimitive.ItemIndicator ref={ref} className={clsx("inline-flex w-[18px] h-[18px]", className)} {...props}>
		{children}
	</DropdownMenuPrimitive.ItemIndicator>
));

DropdownMenuItemIndicator.displayName = DropdownMenuPrimitive.ItemIndicator.displayName;

export {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuCheckboxItem,
	DropdownMenuRadioItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuGroup,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuRadioGroup,
	DropdownMenuItemIndicator,
};
